import { useState, useEffect, useRef } from 'react'

const BOUNDS = { south: 40.58, west: 22.85, north: 40.70, east: 23.00 }

const TYPES = {
  paper: { label: 'Χαρτί', color: '#2196F3', emoji: '📄' },
  plastic: { label: 'Πλαστικό', color: '#FF9800', emoji: '🧴' },
  glass: { label: 'Γυαλί', color: '#9C27B0', emoji: '🍾' },
  clothes: { label: 'Ρούχα', color: '#E91E63', emoji: '👕' },
  electronics: { label: 'Ηλεκτρονικά', color: '#607D8B', emoji: '📱' },
  mixed: { label: 'Μεικτό', color: '#1A6B3C', emoji: '♻️' },
}

function getBinType(tags) {
  if (tags['recycling:paper'] === 'yes') return 'paper'
  if (tags['recycling:plastic'] === 'yes' || tags['recycling:plastic_bottles'] === 'yes') return 'plastic'
  if (tags['recycling:glass'] === 'yes' || tags['recycling:glass_bottles'] === 'yes') return 'glass'
  if (tags['recycling:clothes'] === 'yes') return 'clothes'
  if (tags['recycling:electrical_appliances'] === 'yes') return 'electronics'
  return 'mixed'
}

function getDistance(lat1, lng1, lat2, lng2) {
  const R = 6371000
  const dLat = (lat2 - lat1) * Math.PI / 180
  const dLng = (lng2 - lng1) * Math.PI / 180
  const a = Math.sin(dLat/2)**2 + Math.cos(lat1*Math.PI/180)*Math.cos(lat2*Math.PI/180)*Math.sin(dLng/2)**2
  return Math.round(R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a)))
}

export default function MapPage() {
  const mapRef = useRef(null)
  const mapInstanceRef = useRef(null)
  const markersLayerRef = useRef(null)
  const [bins, setBins] = useState([])
  const [userPos, setUserPos] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [filter, setFilter] = useState('all')
  const [nearbyBins, setNearbyBins] = useState([])
  const [geoStatus, setGeoStatus] = useState('pending') // pending | ok | denied

  useEffect(() => {
    if (!document.getElementById('leaflet-css')) {
      const link = document.createElement('link')
      link.id = 'leaflet-css'
      link.rel = 'stylesheet'
      link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css'
      document.head.appendChild(link)
    }
  }, [])

  // Geolocation
  useEffect(() => {
    if (!navigator.geolocation) { setGeoStatus('denied'); return }
    setGeoStatus('pending')
    navigator.geolocation.getCurrentPosition(
      pos => {
        setUserPos({ lat: pos.coords.latitude, lng: pos.coords.longitude })
        setGeoStatus('ok')
      },
      () => {
        setUserPos({ lat: 40.6401, lng: 22.9444 })
        setGeoStatus('denied')
      },
      { enableHighAccuracy: true, timeout: 10000 }
    )
  }, [])

  // Fetch bins
  useEffect(() => {
    async function fetchBins() {
      try {
        const query = `[out:json][timeout:25];node["amenity"="recycling"](${BOUNDS.south},${BOUNDS.west},${BOUNDS.north},${BOUNDS.east});out body;`
        const res = await fetch(`https://overpass-api.de/api/interpreter?data=${encodeURIComponent(query)}`)
        const data = await res.json()
        setBins(data.elements.map(el => ({
          id: el.id, lat: el.lat, lng: el.lon,
          type: getBinType(el.tags || {}),
          name: el.tags?.name || el.tags?.operator || null,
          tags: el.tags || {}
        })))
      } catch { setError('Αδυναμία φόρτωσης κάδων.') }
      finally { setLoading(false) }
    }
    fetchBins()
  }, [])

  // Nearby bins
  useEffect(() => {
    if (!userPos || bins.length === 0) return
    const nearby = bins
      .map(b => ({ ...b, distance: getDistance(userPos.lat, userPos.lng, b.lat, b.lng) }))
      .filter(b => b.distance < 500)
      .sort((a, b) => a.distance - b.distance)
      .slice(0, 5)
    setNearbyBins(nearby)
  }, [userPos, bins])

  // Init map
  useEffect(() => {
    if (loading || !mapRef.current || mapInstanceRef.current) return
    const loadLeaflet = async () => {
      if (!window.L) {
        await new Promise((res, rej) => {
          const s = document.createElement('script')
          s.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js'
          s.onload = res; s.onerror = rej
          document.head.appendChild(s)
        })
      }
      const L = window.L
      const center = userPos || { lat: 40.6401, lng: 22.9444 }
      const map = L.map(mapRef.current).setView([center.lat, center.lng], 14)
      mapInstanceRef.current = map
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '© OpenStreetMap'
      }).addTo(map)

      // User marker
      if (userPos) {
        const userIcon = L.divIcon({
          html: `<div style="width:16px;height:16px;background:#2196F3;border:3px solid white;border-radius:50%;box-shadow:0 2px 6px rgba(0,0,0,0.4)"></div>`,
          iconSize: [16,16], iconAnchor: [8,8], className: ''
        })
        L.marker([center.lat, center.lng], { icon: userIcon }).addTo(map).bindPopup('📍 Η θέση σου')
      }

      markersLayerRef.current = L.layerGroup().addTo(map)
      renderMarkers(L, bins, 'all', userPos)
    }
    loadLeaflet()
  }, [loading, bins, userPos])

  // Re-render markers on filter change
  useEffect(() => {
    if (!mapInstanceRef.current || !window.L || !markersLayerRef.current) return
    renderMarkers(window.L, bins, filter, userPos)
  }, [filter, bins, userPos])

  function renderMarkers(L, allBins, currentFilter, pos) {
    if (!markersLayerRef.current) return
    markersLayerRef.current.clearLayers()
    const filtered = currentFilter === 'all' ? allBins : allBins.filter(b => b.type === currentFilter)
    filtered.forEach(bin => {
      const t = TYPES[bin.type] || TYPES.mixed
      const dist = pos ? getDistance(pos.lat, pos.lng, bin.lat, bin.lng) : null
      const icon = L.divIcon({
        html: `<div style="width:26px;height:26px;background:${t.color};border:2px solid white;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:13px;box-shadow:0 2px 4px rgba(0,0,0,0.3)">${t.emoji}</div>`,
        iconSize: [26,26], iconAnchor: [13,13], className: ''
      })
      L.marker([bin.lat, bin.lng], { icon })
        .addTo(markersLayerRef.current)
        .bindPopup(`<b>${t.emoji} ${t.label}</b>${bin.name ? `<br>${bin.name}` : ''}${dist ? `<br>📍 ${dist}μ από σένα` : ''}`)
    })
  }

  const filteredNearby = filter === 'all' ? nearbyBins : nearbyBins.filter(b => b.type === filter)
  const displayedCount = filter === 'all' ? bins.length : bins.filter(b => b.type === filter).length

  return (
    <div style={s.page}>
      {/* Geo status */}
      {geoStatus === 'denied' && (
        <div style={s.geoWarn}>⚠️ Δεν επιτράπηκε η πρόσβαση στην τοποθεσία. Εμφανίζεται το κέντρο Θεσσαλονίκης.</div>
      )}

      {/* Filters */}
      <div style={s.filters}>
        <button style={{...s.chip,...(filter==='all'?s.chipActive:{})}} onClick={()=>setFilter('all')}>♻️ Όλοι</button>
        {Object.entries(TYPES).map(([k,v]) => (
          <button key={k} style={{...s.chip,...(filter===k?s.chipActive:{})}} onClick={()=>setFilter(k)}>
            {v.emoji} {v.label}
          </button>
        ))}
      </div>

      {/* Map */}
      <div style={s.mapWrap}>
        {loading && (
          <div style={s.overlay}>
            <div style={s.spinner}/>
            <p style={{color:'#1A6B3C',marginTop:12,fontSize:14}}>Φόρτωση κάδων από OpenStreetMap...</p>
          </div>
        )}
        {error && <div style={s.errorBox}>{error}</div>}
        <div ref={mapRef} style={s.map}/>
      </div>

      {/* Stats */}
      {!loading && (
        <div style={s.stats}>
          <span>🗺️ <b>{displayedCount}</b> κάδοι {filter !== 'all' ? `(${TYPES[filter]?.label})` : 'στη Θεσσαλονίκη'}</span>
          <span style={{color: nearbyBins.length > 0 ? '#1A6B3C' : '#888'}}>
            📍 <b>{filteredNearby.length}</b> κοντά σου
          </span>
        </div>
      )}

      {/* Nearby list */}
      {filteredNearby.length > 0 && (
        <div style={s.card}>
          <h3 style={s.cardTitle}>Κοντινοί κάδοι (&lt;500μ)</h3>
          {filteredNearby.map(bin => {
            const t = TYPES[bin.type] || TYPES.mixed
            return (
              <div key={bin.id} style={s.nearbyItem}>
                <div style={{...s.dot, background: t.color}}>{t.emoji}</div>
                <div style={s.nearbyInfo}>
                  <div style={s.nearbyType}>{t.label}</div>
                  {bin.name && <div style={s.nearbyName}>{bin.name}</div>}
                </div>
                <div style={{...s.dist, color: bin.distance < 100 ? '#1A6B3C' : '#888'}}>
                  {bin.distance}μ
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

const s = {
  page: { display:'flex', flexDirection:'column', gap:'0.75rem', padding:'0.75rem', maxWidth:480, margin:'0 auto' },
  geoWarn: { background:'#fff8e1', border:'1px solid #ffd54f', borderRadius:8, padding:'8px 12px', fontSize:13, color:'#854F0B' },
  filters: { display:'flex', gap:6, overflowX:'auto', paddingBottom:4, scrollbarWidth:'none' },
  chip: { padding:'6px 12px', borderRadius:20, border:'1px solid #ddd', background:'white', fontSize:12, cursor:'pointer', whiteSpace:'nowrap', color:'#555', flexShrink:0 },
  chipActive: { background:'#1A6B3C', color:'white', borderColor:'#1A6B3C' },
  mapWrap: { position:'relative', borderRadius:16, overflow:'hidden', height:320, background:'#f0f0f0' },
  map: { width:'100%', height:'100%' },
  overlay: { position:'absolute', inset:0, zIndex:999, background:'rgba(255,255,255,0.9)', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center' },
  spinner: { width:36, height:36, border:'3px solid #e8f5e9', borderTop:'3px solid #1A6B3C', borderRadius:'50%', animation:'spin 1s linear infinite' },
  errorBox: { position:'absolute', inset:0, zIndex:999, display:'flex', alignItems:'center', justifyContent:'center', background:'#fff', color:'#dc2626', fontSize:14, padding:'1rem', textAlign:'center' },
  stats: { background:'white', borderRadius:12, padding:'10px 14px', display:'flex', justifyContent:'space-between', fontSize:13, color:'#555', boxShadow:'0 2px 8px rgba(0,0,0,0.06)' },
  card: { background:'white', borderRadius:16, padding:'1rem', boxShadow:'0 2px 12px rgba(0,0,0,0.06)' },
  cardTitle: { margin:'0 0 12px', fontSize:15, fontWeight:600, color:'#333' },
  nearbyItem: { display:'flex', alignItems:'center', gap:12, padding:'10px 0', borderBottom:'1px solid #f5f5f5' },
  dot: { width:36, height:36, borderRadius:'50%', display:'flex', alignItems:'center', justifyContent:'center', fontSize:18, flexShrink:0 },
  nearbyInfo: { flex:1 },
  nearbyType: { fontWeight:600, fontSize:14, color:'#333' },
  nearbyName: { fontSize:12, color:'#888' },
  dist: { fontSize:15, fontWeight:700, flexShrink:0 },
}
